import { Injectable, signal, inject, effect } from '@angular/core';
import { EMPTY } from 'rxjs';
import { switchMap, tap, catchError } from 'rxjs/operators';
import { toObservable, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ProductType, SheetItem } from './sidebar.models';
import { DevUserService } from '../features/admin/dev-user.service';
import { AdminApiService, SpreadsheetDb } from '../features/admin/admin-api.service';
import { RateApiService } from '../core/services/rate-api.service';
import { SessionContextService } from '../core/services/session-context.service';

const DB_TO_TYPE: Record<string, ProductType> = { fa: 'fa', ia: 'ia', iva: 'rila' };
const DB_LABELS: Record<string, string> = { fa: 'FA', ia: 'IA', iva: 'RILA' };

@Injectable({ providedIn: 'root' })
export class SidebarService {
  private devUser = inject(DevUserService);
  private adminApi = inject(AdminApiService);
  private rateApi = inject(RateApiService);
  private session = inject(SessionContextService);

  collapsed = false;
  activeType = signal<ProductType>('ia');
  activeSheetId = 'all-shelf';
  mineSectionCollapsed = false;
  companySectionCollapsed = false;

  overviewItem: SheetItem = {
    id: 'all-shelf',
    type: 'all',
    typeDisplay: 'ALL',
    name: 'All Types \u00B7 My Shelf',
    subtitle: 'Change monitor \u00B7 2,847 products',
    hasChanges: true,
  };

  mineSheets: Record<ProductType, SheetItem[]> = {
    all: [], ia: [], fa: [], va: [], rila: [],
  };

  /** Raw company spreadsheet rows from the API. The reactive
   *  `companySheets` signal below filters this by visibility for
   *  the current user (Phase 3D). */
  private rawCompanySheets = signal<any[]>([]);

  /** Spreadsheets visible to the current user, with per-user-group default
   *  marker. Recomputes on any change to fetched data OR session context. */
  readonly companySheets = signal<SheetItem[]>([]);

  constructor() {
    toObservable(this.devUser.currentUser).pipe(
      switchMap(user => {
        if (!user?.user_company_id) {
          this.rawCompanySheets.set([]);
          return EMPTY;
        }
        return this.adminApi.getSpreadsheets(user.user_company_id).pipe(
          tap((sheets: any[]) => {
            this.rawCompanySheets.set(sheets);
            this.preloadRateData(sheets);
          }),
          catchError(() => {
            this.rawCompanySheets.set([]);
            return EMPTY;
          }),
        );
      }),
      takeUntilDestroyed(),
    ).subscribe();

    // Re-derive companySheets whenever raw data OR session context changes,
    // so post-fetch session hydration causes an automatic refilter.
    effect(() => {
      const raw = this.rawCompanySheets();
      const ctx = this.session.context();   // subscribe to session updates
      const items: SheetItem[] = [];
      for (const s of raw) {
        if (s.inactive) continue;
        const db = s.db as SpreadsheetDb;
        const visible = this.session.isSpreadsheetVisibleByDb(db, s.id, !!s.status);
        if (!visible) continue;
        const isDefault = this.session.isSpreadsheetDefaultByDb(db, s.id, !!s.defaultSheet);
        items.push({
          id: `ss-${s.db}-${s.id}`,
          type: DB_TO_TYPE[s.db] ?? 'co' as any,
          typeDisplay: DB_LABELS[s.db] ?? s.db?.toUpperCase() ?? '',
          name: s.name,
          subtitle: isDefault ? 'Default sheet' : s.type ?? '',
          locked: false,
          db: s.db,
          ssId: s.id,
          companyId: ctx?.companyId,
        });
      }
      this.companySheets.set(items);
    });
  }

  toggleSidebar(): void {
    this.collapsed = !this.collapsed;
  }

  setActiveType(type: ProductType): void {
    this.activeType.set(type);
  }

  setActiveSheet(id: string): void {
    this.activeSheetId = id;
  }

  toggleMineSection(): void {
    this.mineSectionCollapsed = !this.mineSectionCollapsed;
  }

  toggleCompanySection(): void {
    this.companySectionCollapsed = !this.companySectionCollapsed;
  }

  /** Fire-and-forget: warm the rate cache for all sheet types this company has.
   *
   *  Deferred to browser idle time. These feeds are multi-MB each, and firing
   *  all three the moment the sidebar's spreadsheet list lands put them in
   *  direct competition with whatever the current route is fetching — on
   *  /rates that meant the IA feed the user is staring at queued behind two
   *  downloads for tabs they haven't opened, plus their map/dedupe work on the
   *  same thread the grid renders on. Idle-scheduling keeps the warm-up
   *  (spreadsheet preview loads instantly) without paying for it up front. */
  private preloadRateData(sheets: any[]) {
    const dbs = new Set(sheets.filter(s => !s.inactive).map((s: any) => s.db));
    this.whenIdle(() => {
      if (dbs.has('fa'))  this.rateApi.getFixedAnnuityRates();
      if (dbs.has('ia'))  this.rateApi.getIndexedAnnuityRates();
      if (dbs.has('iva')) this.rateApi.getIndexedVariableRates();
    });
  }

  /** requestIdleCallback where available (Safari still lacks it), with a plain
   *  timeout fallback. The timeout cap means the warm-up still happens on a
   *  page that never goes idle. */
  private whenIdle(fn: () => void): void {
    const ric = (globalThis as any).requestIdleCallback as
      | ((cb: () => void, opts?: { timeout: number }) => number)
      | undefined;
    if (ric) ric(() => fn(), { timeout: 4000 });
    else setTimeout(fn, 2000);
  }
}
